import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Avatar,
  LinearProgress,
  Badge,
  Fade,
  Slide,
  Zoom,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Router as RouterIcon,
  AccountBalance as BalanceIcon,
  DateRange as DateIcon,
  Security as SecurityIcon,
  SignalWifiStatusbar4Bar as SignalIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  NetworkCheck as NetworkCheckIcon,
  Speed as SpeedIcon,
  Timeline as TimelineIcon,
  WifiTethering,
  DeviceHub,
  MonitorHeart,
  VerifiedUser,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';
import customerService from '../services/customerService';
import api from '../services/api';

interface ViewCustomerDialogProps {
  open: boolean;
  onClose: () => void;
  customer: any;
}

interface PPPSecret {
  id: string;
  username: string;
  profile: string;
  status: 'online' | 'offline';
  ipAddress?: string;
  uptime?: string;
  callerID?: string | null;
  lastLogin?: string;
}

const ViewCustomerDialog: React.FC<ViewCustomerDialogProps> = ({
  open,
  onClose,
  customer,
}) => {
  const [pppSecrets, setPppSecrets] = useState<PPPSecret[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [onuStatus, setOnuStatus] = useState<any | null>(null);
  const [onuLoading, setOnuLoading] = useState(false);
  const [onuError, setOnuError] = useState<string | null>(null);

  // Fungsi untuk ambil 5 segmen awal MAC Address (tanpa case sensitive)
  const getMacPrefix = (mac?: string) => {
    if (!mac) return '';
    
    // Normalize MAC address - hapus spasi dan karakter non-hex
    const cleanMac = mac.toLowerCase().replace(/[^0-9a-f:]/g, '');
    
    // Split by colon and take first 5 segments
    const segments = cleanMac.split(':').filter(segment => segment.length > 0);
    
    // Pastikan ada minimal 5 segmen
    if (segments.length < 5) {
      console.warn('MAC address tidak memiliki cukup segmen:', mac, 'segments:', segments);
      return '';
    }
    
    return segments.slice(0, 5).join(':');
  };

  const fetchPPPSecrets = async () => {
    if (!customer?.id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await customerService.fetchPPPSecrets(customer.id);
      setPppSecrets(data);
    } catch (err) {
      console.error('Error fetching PPP secrets:', err);
      setError('Failed to fetch PPP secrets');
      // Fallback to mock data
      const mockSecrets: PPPSecret[] = [
        {
          id: '1',
          username: customer.username,
          profile: customer.package?.name || 'Default',
          status: customer.status === 'active' ? 'online' : 'offline',
          ipAddress: '192.168.1.100',
          uptime: '2d 14h 30m',
          callerID: null,
          lastLogin: new Date().toISOString(),
        },
      ];
      setPppSecrets(mockSecrets);
    } finally {
      setLoading(false);
    }
  };

  // Fetch status ONU dari OLT berdasarkan prefix MAC Address
  const fetchOnuStatus = useCallback(async () => {
    console.log('=== FETCH ONU STATUS STARTED ===');
    console.log('Customer raw data:', customer);
    console.log('PPP Secrets data:', pppSecrets);
    
    setOnuStatus(null);
    setOnuLoading(true);
    setOnuError(null);
    try {
      // Ambil callerID dari PPP Secret yang aktif
      const activePppSecret = pppSecrets.find(secret => secret.status === 'online');
      const callerID = activePppSecret?.callerID || pppSecrets[0]?.callerID;
      
      console.log('=== CALLER ID FROM PPP ===');
      console.log('Active PPP Secret:', activePppSecret);
      console.log('CallerID:', callerID);
      
      if (!callerID) {
        console.log('ERROR: CallerID tidak ditemukan di PPP Secret');
        setOnuError('CallerID tidak ditemukan di PPP Secret. Pastikan customer memiliki callerID yang valid.');
        setOnuLoading(false);
        return;
      }
      
      const macPrefix = getMacPrefix(callerID);
      console.log('MAC Prefix from CallerID:', macPrefix);
      
      if (!macPrefix) {
        console.log('ERROR: Format CallerID tidak valid');
        setOnuError('Format CallerID tidak valid. CallerID harus berupa MAC address dengan minimal 5 segmen.');
        setOnuLoading(false);
        return;
      }
      
      // Cari OLT berdasarkan SPOP customer
      if (!customer?.spop?.id) {
        console.log('ERROR: Customer tidak memiliki SPOP');
        setOnuError('Customer tidak memiliki SPOP. Pastikan customer terhubung ke SPOP yang valid.');
        setOnuLoading(false);
        return;
      }
      
      console.log('=== SEARCHING OLT BY SPOP ===');
      console.log('Customer SPOP ID:', customer.spop.id);
      
      // Fetch OLT berdasarkan SPOP
      const oltResponse = await api.get(`/olts/spop/${customer.spop.id}`);
      const olts = oltResponse.data?.data || [];
      
      console.log('Found OLTs for SPOP:', olts);
      
      if (!olts || olts.length === 0) {
        console.log('ERROR: Tidak ada OLT ditemukan untuk SPOP ini');
        setOnuError('Tidak ada OLT ditemukan untuk SPOP customer. Pastikan SPOP memiliki OLT yang terkonfigurasi.');
        setOnuLoading(false);
        return;
      }
      
      // Ambil OLT pertama (atau bisa dimodifikasi untuk cek semua OLT)
      const olt = olts[0];
      console.log('Using OLT:', olt);
      
      // Cek semua port PON (biasanya 0/1/1 sampai 0/1/16 untuk OLT 16 port)
      const ponPorts = [];
      for (let i = 1; i <= 16; i++) {
        ponPorts.push(`0/1/${i}`);
      }
      
      console.log('=== CHECKING ALL PON PORTS ===');
      console.log('PON Ports to check:', ponPorts);
      
      let foundOnu = null;
      
      for (const ponId of ponPorts) {
        try {
          console.log(`Checking PON ${ponId}...`);
          
          const response = await api.get(`/olts/${olt.id}/onu-data`, { params: { ponId } });
          const onus = response.data?.data?.onus || [];
          
          console.log(`PON ${ponId} - Found ${onus.length} ONUs`);
          
          // Cari ONU dengan prefix MAC yang sama
          const matchedOnu = onus.find((onu: any) => {
            const onuMacPrefix = getMacPrefix(onu.macAddress);
            console.log(`Comparing: ${onuMacPrefix} vs ${macPrefix}`);
            return onuMacPrefix === macPrefix;
          });
          
          if (matchedOnu) {
            foundOnu = { ...matchedOnu, ponId, oltId: olt.id };
            console.log(`MATCH FOUND on PON ${ponId}:`, matchedOnu);
            break;
          }
        } catch (ponError: any) {
          console.warn(`Error checking PON ${ponId}:`, ponError?.response?.data?.message || ponError?.message || ponError);
          // Continue checking other PON ports
        }
      }
      
      console.log('=== FINAL RESULT ===');
      console.log('Found ONU:', foundOnu);
      
      setOnuStatus(foundOnu || null);
    } catch (err: any) {
      console.error('FETCH ONU DATA ERROR:', {
        error: err,
        response: err?.response,
        message: err?.message,
        status: err?.response?.status,
        data: err?.response?.data
      });
      setOnuError(err?.response?.data?.message || err.message || 'Gagal mengambil status ONU');
    } finally {
      setOnuLoading(false);
    }
  }, [customer, pppSecrets]);

  useEffect(() => {
    if (open && customer) {
      fetchPPPSecrets();
    }
  }, [open, customer]);

  // Fetch ONU status setelah PPP secrets dimuat
  useEffect(() => {
    if (open && customer && pppSecrets.length > 0) {
      fetchOnuStatus();
    }
  }, [open, customer, pppSecrets, fetchOnuStatus]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'default';
      case 'suspended':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Aktif';
      case 'inactive':
        return 'Non-aktif';
      case 'suspended':
        return 'Ditangguhkan';
      default:
        return status;
    }
  };

  const getPPPStatusColor = (status: string) => {
    return status === 'online' ? 'success' : 'error';
  };

  const getPPPStatusLabel = (status: string) => {
    return status === 'online' ? 'Online' : 'Offline';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon color="success" />;
      case 'inactive':
        return <BlockIcon color="disabled" />;
      case 'suspended':
        return <BlockIcon color="error" />;
      default:
        return <BlockIcon color="disabled" />;
    }
  };

  const getSignalIcon = (signalQuality?: string) => {
    if (!signalQuality) return <SignalIcon color="disabled" />;
    
    if (signalQuality.includes('excellent') || signalQuality.includes('very_strong')) {
      return <SignalIcon color="success" />;
    } else if (signalQuality.includes('good') || signalQuality.includes('strong')) {
      return <SignalIcon color="primary" />;
    } else if (signalQuality.includes('fair') || signalQuality.includes('medium')) {
      return <SignalIcon color="warning" />;
    } else {
      return <SignalIcon color="error" />;
    }
  };

  if (!customer) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="xl" 
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          position: 'relative',
          maxHeight: '95vh',
          height: 'auto',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(10px)',
          }
        }
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar
                sx={{ 
                  bgcolor: customer.status === 'active' ? 'success.main' : 'error.main',
                  width: 56,
                  height: 56,
                  border: '3px solid white',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
                }}
              >
                <PersonIcon fontSize="large" />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight="bold" color="primary.main">
                  {customer.name}
                </Typography>
                <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                  {getStatusIcon(customer.status)}
                  <Chip
                    label={getStatusLabel(customer.status)}
                    color={getStatusColor(customer.status) as any}
                    size="small"
                    sx={{ fontWeight: 'bold' }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    #{customer.customer_code}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <IconButton 
              onClick={onClose} 
              size="large"
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ 
          bgcolor: 'background.default', 
          p: 3,
          overflow: 'visible',
          maxHeight: 'none'
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Quick Stats Row */}
            <Fade in={true} timeout={800}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2, mb: 2 }}>
                <Card 
                  sx={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 2 }}>
                    <Badge color="success" variant="dot" invisible={pppSecrets.filter(s => s.status === 'online').length === 0}>
                      <SignalIcon sx={{ fontSize: 40, mb: 1 }} />
                    </Badge>
                    <Typography variant="h4" fontWeight="bold">
                      {pppSecrets.filter(s => s.status === 'online').length}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Active Sessions
                    </Typography>
                  </CardContent>
                </Card>
                <Card 
                  sx={{ 
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    color: 'white',
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 2 }}>
                    <BalanceIcon sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.2rem' }}>
                      {formatCurrency(customer.balance || 0)}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Current Balance
                    </Typography>
                  </CardContent>
                </Card>
                <Card 
                  sx={{ 
                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                    color: 'white',
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 2 }}>
                    <DateIcon sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h4" fontWeight="bold">
                      {customer.activation_date 
                        ? Math.floor((new Date().getTime() - new Date(customer.activation_date).getTime()) / (1000 * 60 * 60 * 24))
                        : 0
                      }
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Days Active
                    </Typography>
                  </CardContent>
                </Card>
                <Card 
                  sx={{ 
                    background: onuStatus?.isOnline 
                      ? 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)' 
                      : 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
                    color: 'primary.main',
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 2 }}>
                    {getSignalIcon(onuStatus?.signalQuality)}
                    <Typography variant="h6" fontWeight="bold" sx={{ mt: 1 }}>
                      {onuStatus?.status || 'Unknown'}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      ONU Status
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            </Fade>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
              {/* Customer Information */}
              <Slide direction="right" in={true} timeout={600}>
                <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box display="flex" alignItems="center" gap={2} mb={3}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                        <PersonIcon />
                      </Avatar>
                      <Typography variant="h6" fontWeight="bold">
                        Informasi Pelanggan
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 3 }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary" fontWeight="medium">
                          Kode Pelanggan
                        </Typography>
                        <Typography variant="h6" fontWeight="bold" color="primary.main">
                          {customer.customer_code}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary" fontWeight="medium">
                          Username
                        </Typography>
                        <Typography variant="h6" fontWeight="bold">
                          {customer.username}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary" fontWeight="medium">
                          Email
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {customer.email}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary" fontWeight="medium">
                          Nomor Telepon
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {customer.phone}
                        </Typography>
                      </Box>
                      <Box sx={{ gridColumn: '1 / -1' }}>
                        <Typography variant="body2" color="text.secondary" fontWeight="medium">
                          Alamat
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {customer.address || '-'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary" fontWeight="medium">
                          Tanggal Aktivasi
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {customer.activation_date 
                            ? format(new Date(customer.activation_date), 'dd MMMM yyyy', { locale: id })
                            : '-'
                          }
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary" fontWeight="medium">
                          Tanggal Daftar
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {format(new Date(customer.created_at), 'dd MMMM yyyy', { locale: id })}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Slide>

              {/* ONU Status Card */}
              <Slide direction="left" in={true} timeout={800}>
                <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.1)', height: 'fit-content' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box display="flex" alignItems="center" gap={2} mb={3}>
                      <Avatar sx={{ bgcolor: onuStatus?.isOnline ? 'success.main' : 'error.main', width: 48, height: 48 }}>
                        <NetworkCheckIcon />
                      </Avatar>
                      <Typography variant="h6" fontWeight="bold">
                        Status ONU dari OLT
                      </Typography>
                    </Box>
                    {onuLoading ? (
                      <Box display="flex" flexDirection="column" alignItems="center" p={2}>
                        <CircularProgress size={40} thickness={4} />
                        <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
                          Mengambil data ONU dari OLT...
                        </Typography>
                        <LinearProgress sx={{ width: '100%', mt: 1 }} />
                      </Box>
                    ) : onuError ? (
                      <Alert severity="warning" sx={{ borderRadius: 2 }}>
                        <Typography variant="body2" component="div">
                          <strong>Error:</strong> {onuError}
                        </Typography>
                      </Alert>
                    ) : onuStatus ? (
                      <Box>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <Chip 
                            label={onuStatus.status || 'Unknown'} 
                            color={onuStatus.isOnline ? 'success' : 'error'} 
                            sx={{ fontWeight: 'bold' }}
                          />
                          {onuStatus.isOnline && (
                            <Chip label="Online" color="success" variant="outlined" size="small" />
                          )}
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
                          <Box p={2} bgcolor="grey.50" borderRadius={2}>
                            <Typography variant="body2" color="text.secondary">MAC Address</Typography>
                            <Typography variant="body1" fontWeight="bold">{onuStatus.macAddress}</Typography>
                          </Box>
                          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            <Box p={2} bgcolor="grey.50" borderRadius={2}>
                              <Typography variant="body2" color="text.secondary">PON</Typography>
                              <Typography variant="body1" fontWeight="bold">{onuStatus.ponId}</Typography>
                            </Box>
                            <Box p={2} bgcolor="grey.50" borderRadius={2}>
                              <Typography variant="body2" color="text.secondary">Distance</Typography>
                              <Typography variant="body1" fontWeight="bold">
                                {onuStatus.distance !== null && onuStatus.distance !== undefined 
                                  ? `${onuStatus.distance} m` 
                                  : 'N/A'
                                }
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            <Box p={2} bgcolor="grey.50" borderRadius={2}>
                              <Typography variant="body2" color="text.secondary">RX Power</Typography>
                              <Typography variant="body1" fontWeight="bold">
                                {onuStatus.rxPower !== null ? onuStatus.rxPower + ' dBm' : '-'}
                              </Typography>
                            </Box>
                            <Box p={2} bgcolor="grey.50" borderRadius={2}>
                              <Typography variant="body2" color="text.secondary">TX Power</Typography>
                              <Typography variant="body1" fontWeight="bold">
                                {onuStatus.txPower !== null ? onuStatus.txPower + ' dBm' : '-'}
                              </Typography>
                            </Box>
                          </Box>
                          {onuStatus && (
                            <Box p={2} bgcolor="grey.50" borderRadius={2}>
                              <Typography variant="body2" color="text.secondary">OLT Model</Typography>
                              <Typography variant="body1" fontWeight="bold">
                                {onuStatus.oltModel || 'Unknown'}
                              </Typography>
                            </Box>
                          )}
                          <Box p={2} bgcolor="grey.50" borderRadius={2}>
                            <Typography variant="body2" color="text.secondary">Signal Quality</Typography>
                            <Box display="flex" alignItems="center" gap={1}>
                              {getSignalIcon(onuStatus.signalQuality)}
                              <Typography variant="body1" fontWeight="bold">{onuStatus.signalQuality}</Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    ) : (
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        <Typography variant="body2">
                          Tidak ada data ONU yang cocok dengan MAC address customer.
                        </Typography>
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Slide>

              {/* PPP Secrets Card */}
              <Slide direction="up" in={true} timeout={1000}>
                <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.1)', height: 'fit-content' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box display="flex" alignItems="center" gap={2} mb={3}>
                      <Avatar sx={{ bgcolor: 'secondary.main', width: 48, height: 48 }}>
                        <SecurityIcon />
                      </Avatar>
                      <Typography variant="h6" fontWeight="bold">
                        PPP Secrets
                      </Typography>
                    </Box>
                    
                    {loading ? (
                      <Box display="flex" flexDirection="column" alignItems="center" p={2}>
                        <CircularProgress size={40} thickness={4} />
                        <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
                          Loading PPP data...
                        </Typography>
                        <LinearProgress sx={{ width: '100%', mt: 1 }} />
                      </Box>
                    ) : error ? (
                      <Alert severity="error" sx={{ borderRadius: 2 }}>
                        <Typography variant="body2" component="div">
                          <strong>Error:</strong> {error}
                        </Typography>
                      </Alert>
                    ) : pppSecrets.length === 0 ? (
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        <Typography variant="body2">
                          Tidak ada PPP Secret yang ditemukan
                        </Typography>
                      </Alert>
                    ) : (
                      <Box>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <Chip 
                            label={`${pppSecrets.filter(s => s.status === 'online').length} Online`} 
                            color="success" 
                            sx={{ fontWeight: 'bold' }}
                          />
                          <Chip 
                            label={`${pppSecrets.filter(s => s.status === 'offline').length} Offline`} 
                            color="error" 
                            variant="outlined" 
                            size="small" 
                          />
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
                          {pppSecrets.map((secret, index) => (
                            <Box key={secret.id} sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1.5 }}>
                              <Box p={2} bgcolor="grey.50" borderRadius={2}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                                    {secret.username.charAt(0).toUpperCase()}
                                  </Avatar>
                                  <Typography variant="body2" fontWeight="bold">
                                    {secret.username}
                                  </Typography>
                                  <Chip
                                    label={getPPPStatusLabel(secret.status)}
                                    color={getPPPStatusColor(secret.status) as any}
                                    size="small"
                                    sx={{ ml: 'auto', fontSize: '0.7rem', height: 20 }}
                                  />
                                </Box>
                              </Box>
                              <Box p={2} bgcolor="grey.50" borderRadius={2}>
                                <Typography variant="body2" color="text.secondary">Profile</Typography>
                                <Typography variant="body1" fontWeight="bold">{secret.profile}</Typography>
                              </Box>
                              <Box p={2} bgcolor="grey.50" borderRadius={2}>
                                <Typography variant="body2" color="text.secondary">IP Address</Typography>
                                <Typography variant="body1" fontWeight="bold" fontFamily="monospace">
                                  {secret.ipAddress || '-'}
                                </Typography>
                              </Box>
                              <Box p={2} bgcolor="grey.50" borderRadius={2}>
                                <Typography variant="body2" color="text.secondary">Caller ID</Typography>
                                <Typography variant="body1" fontWeight="bold" fontFamily="monospace">
                                  {secret.callerID || '-'}
                                </Typography>
                              </Box>
                              <Box p={2} bgcolor="grey.50" borderRadius={2}>
                                <Typography variant="body2" color="text.secondary">Uptime</Typography>
                                <Typography variant="body1" fontWeight="bold">{secret.uptime || '-'}</Typography>
                              </Box>
                            </Box>
                          ))}
                          <Box p={2} bgcolor="grey.50" borderRadius={2}>
                            <Typography variant="body2" color="text.secondary">Total Sessions</Typography>
                            <Typography variant="body1" fontWeight="bold">{pppSecrets.length}</Typography>
                          </Box>
                          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            <Box p={2} bgcolor="grey.50" borderRadius={2}>
                              <Typography variant="body2" color="text.secondary">Online</Typography>
                              <Typography variant="body1" fontWeight="bold" color="success.main">
                                {pppSecrets.filter(s => s.status === 'online').length}
                              </Typography>
                            </Box>
                            <Box p={2} bgcolor="grey.50" borderRadius={2}>
                              <Typography variant="body2" color="text.secondary">Offline</Typography>
                              <Typography variant="body1" fontWeight="bold" color="error.main">
                                {pppSecrets.filter(s => s.status === 'offline').length}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Slide>
            </Box>

            {/* Package & Network Information */}
            <Zoom in={true} timeout={1000}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" gap={2} mb={3}>
                    <Avatar sx={{ bgcolor: 'success.main', width: 48, height: 48 }}>
                      <RouterIcon />
                    </Avatar>
                    <Typography variant="h6" fontWeight="bold">
                      Informasi Paket & Jaringan
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 3 }}>
                    <Card variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'primary.50' }}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                          <SpeedIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" color="text.secondary">Paket</Typography>
                          <Typography variant="h6" fontWeight="bold">{customer.package?.name || '-'}</Typography>
                          <Typography variant="body2" color="success.main" fontWeight="medium">
                            {customer.package?.price ? formatCurrency(customer.package.price) : ''}
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                    <Card variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'success.50' }}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar sx={{ bgcolor: 'success.main', width: 40, height: 40 }}>
                          <TrendingUpIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" color="text.secondary">Download</Typography>
                          <Typography variant="h6" fontWeight="bold">{customer.package?.bandwidth_download || '-'}</Typography>
                        </Box>
                      </Box>
                    </Card>
                    <Card variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'warning.50' }}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar sx={{ bgcolor: 'warning.main', width: 40, height: 40 }}>
                          <TrendingUpIcon sx={{ transform: 'rotate(180deg)' }} />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" color="text.secondary">Upload</Typography>
                          <Typography variant="h6" fontWeight="bold">{customer.package?.bandwidth_upload || '-'}</Typography>
                        </Box>
                      </Box>
                    </Card>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mt: 3 }}>
                    <Box p={2} bgcolor="grey.50" borderRadius={2}>
                      <Typography variant="body2" color="text.secondary">Router</Typography>
                      <Typography variant="body1" fontWeight="bold">{customer.router?.name || '-'}</Typography>
                    </Box>
                    <Box p={2} bgcolor="grey.50" borderRadius={2}>
                      <Typography variant="body2" color="text.secondary">SPOP</Typography>
                      <Typography variant="body1" fontWeight="bold">{customer.spop?.name || '-'}</Typography>
                    </Box>
                    <Box p={2} bgcolor="grey.50" borderRadius={2}>
                      <Typography variant="body2" color="text.secondary">ODP</Typography>
                      <Typography variant="body1" fontWeight="bold">{customer.odp?.fullCode || '-'}</Typography>
                    </Box>
                    <Box p={2} bgcolor="grey.50" borderRadius={2}>
                      <Typography variant="body2" color="text.secondary">Profile</Typography>
                      <Typography variant="body1" fontWeight="bold">{customer.profile}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Zoom>

            {/* Technical Details */}
            <Zoom in={true} timeout={1400}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" gap={2} mb={3}>
                    <Avatar sx={{ bgcolor: 'info.main', width: 48, height: 48 }}>
                      <DeviceHub />
                    </Avatar>
                    <Typography variant="h6" fontWeight="bold">
                      Detail Teknis
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2 }}>
                    <Box p={2} bgcolor="grey.50" borderRadius={2}>
                      <Typography variant="body2" color="text.secondary">IP Address</Typography>
                      <Typography variant="body1" fontWeight="bold" fontFamily="monospace">
                        {customer.ip_address || '-'}
                      </Typography>
                    </Box>
                    <Box p={2} bgcolor="grey.50" borderRadius={2}>
                      <Typography variant="body2" color="text.secondary">MAC Address</Typography>
                      <Typography variant="body1" fontWeight="bold" fontFamily="monospace">
                        {customer.mac_address || '-'}
                      </Typography>
                    </Box>
                    <Box p={2} bgcolor="grey.50" borderRadius={2}>
                      <Typography variant="body2" color="text.secondary">OLT ID</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {customer?.spop?.olt_id || (customer?.spop?.olts && customer.spop.olts.length > 0 ? customer.spop.olts[0].id : '') || customer?.router?.olt_id || customer?.oltId || customer?.olt_id || '-'}
                      </Typography>
                    </Box>
                    <Box p={2} bgcolor="grey.50" borderRadius={2}>
                      <Typography variant="body2" color="text.secondary">PON ID</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {customer?.pon_id || customer?.ponId || customer?.odp?.pon_id || customer?.router?.pon_id || '-'}
                      </Typography>
                    </Box>
                    <Box p={2} bgcolor="grey.50" borderRadius={2}>
                      <Typography variant="body2" color="text.secondary">SPOP OLT</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {(customer?.spop?.olts && customer.spop.olts.length > 0 ? customer.spop.olts[0].name : '') || customer?.spop?.olt_name || '-'}
                      </Typography>
                    </Box>
                    <Box p={2} bgcolor="grey.50" borderRadius={2}>
                      <Typography variant="body2" color="text.secondary">Caller ID</Typography>
                      <Typography variant="body1" fontWeight="bold" fontFamily="monospace">
                        {customer?.callerID || '-'}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Zoom>

            {/* Summary Statistics */}
            <Fade in={true} timeout={1600}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" gap={2} mb={3}>
                    <Avatar sx={{ bgcolor: 'purple.main', width: 48, height: 48 }}>
                      <BalanceIcon />
                    </Avatar>
                    <Typography variant="h6" fontWeight="bold">
                      Ringkasan Statistik
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2 }}>
                    <Card variant="outlined" sx={{ textAlign: 'center', p: 2, borderRadius: 2 }}>
                      <CardContent sx={{ p: 1 }}>
                        <SignalIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                        <Typography variant="h6" fontWeight="bold">
                          {pppSecrets.filter(s => s.status === 'online').length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Active Sessions
                        </Typography>
                      </CardContent>
                    </Card>
                    <Card variant="outlined" sx={{ textAlign: 'center', p: 2, borderRadius: 2 }}>
                      <CardContent sx={{ p: 1 }}>
                        <DateIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
                        <Typography variant="h6" fontWeight="bold">
                          {customer.activation_date 
                            ? Math.floor((new Date().getTime() - new Date(customer.activation_date).getTime()) / (1000 * 60 * 60 * 24))
                            : 0
                          }
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Days Active
                        </Typography>
                      </CardContent>
                    </Card>
                    <Card variant="outlined" sx={{ textAlign: 'center', p: 2, borderRadius: 2 }}>
                      <CardContent sx={{ p: 1 }}>
                        <BalanceIcon color="info" sx={{ fontSize: 40, mb: 1 }} />
                        <Typography variant="h6" fontWeight="bold">
                          {formatCurrency(customer.balance || 0)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Current Balance
                        </Typography>
                      </CardContent>
                    </Card>
                    <Card variant="outlined" sx={{ textAlign: 'center', p: 2, borderRadius: 2 }}>
                      <CardContent sx={{ p: 1 }}>
                        <SecurityIcon color="secondary" sx={{ fontSize: 40, mb: 1 }} />
                        <Typography variant="h6" fontWeight="bold">
                          {pppSecrets.length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total PPP Secrets
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>
                </CardContent>
              </Card>
            </Fade>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, bgcolor: 'grey.50' }}>
          <Button 
            onClick={onClose} 
            variant="contained" 
            size="large"
            sx={{ 
              borderRadius: 3,
              px: 4,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              }
            }}
          >
            Tutup
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default ViewCustomerDialog;
